import { useMemo, type ChangeEvent } from 'react';
import { addMonths, eachMonthOfInterval, format, startOfMonth } from 'date-fns';
import { fr } from 'date-fns/locale';

const MONTH_RANGE = 6;
const MONTH_VALUE_FORMAT = 'yyyy-MM';

const SELECT_CLASS =
    'cursor-pointer rounded-md border border-light-gray px-3 py-2 text-medium font-medium capitalize text-dark-gray';

type MonthOption = Readonly<{ value: string; label: string }>;

function buildMonthSelectOptions(reference: Date): readonly MonthOption[] {
    const start = startOfMonth(addMonths(reference, -MONTH_RANGE));
    const end = startOfMonth(addMonths(reference, MONTH_RANGE));
    return eachMonthOfInterval({ start, end }).map(monthStart => ({
        value: format(monthStart, MONTH_VALUE_FORMAT),
        label: format(monthStart, 'MMMM yyyy', { locale: fr }),
    }));
}

type MonthSelectProps = Readonly<{
    anchorDate: Date;
    onAnchorChange: (next: Date) => void;
}>;

export function MonthSelect({ anchorDate, onAnchorChange }: MonthSelectProps) {
    const monthOptions = useMemo(() => buildMonthSelectOptions(new Date()), []);
    const anchorValue = format(anchorDate, MONTH_VALUE_FORMAT);

    const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
        const [year, month] = event.target.value.split('-').map(Number);
        onAnchorChange(startOfMonth(new Date(year, month - 1, 1)));
    };

    return (
        <select
            className={SELECT_CLASS}
            value={anchorValue}
            onChange={handleChange}
            aria-label="Choisir le mois"
        >
            {monthOptions.map(option => (
                <option key={option.value} value={option.value}>
                    {option.label}
                </option>
            ))}
        </select>
    );
}
